import React, { useState } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import RichTextEditor from '../WidgetEditComponents/RichTextEditor';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function MethodologyWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // Image Upload States
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    const [isUploadingUnderlay, setIsUploadingUnderlay] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        const fakeEvent = {
            target: {
                name: 'layoutType',
                value: layoutId,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Main Content Text Configuration - MIT RICH TEXT
    const mainTextConfig = [
        {
            fieldName: 'mainTitle',
            label: 'Main Title',
            type: 'input',
            placeholder: 'MY METHODOLOGY',
            fontField: 'mainTitleFont',
            fontType: 'big',
            effectField: 'mainTitleEffect'
        },
        {
            fieldName: 'mainDescription',
            label: 'Main Description',
            type: 'textarea',
            placeholder: 'My clients get gorgeous, divine, out-of-this-world results for a reason.',
            rows: 2,
            fontField: 'mainDescriptionFont',
            fontType: 'small',
            effectField: 'mainDescriptionEffect'
        },
        {
            fieldName: 'mainContent',
            label: 'Main Content',
            type: 'richtext',
            placeholder: 'Enter your main content with <strong>formatting</strong>...<br><br>Use HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;em&gt; for rich formatting.',
            height: '250px',
            fontField: 'additionalTextFont',
            fontType: 'small'
        }
    ];

    // Steps Text Configuration
    const stepsTextConfig = [
        {
            fieldName: 'step1Number',
            label: 'Step 1 Number',
            type: 'input',
            placeholder: '01',
            fontField: 'stepNumberFont',
            fontType: 'big',
            effectField: 'stepNumberEffect'
        },
        {
            fieldName: 'step1Title',
            label: 'Step 1 Title',
            type: 'textarea',
            placeholder: 'Body Based Transformational Coaching and Somatics',
            rows: 2,
            fontField: 'stepTitleFont',
            fontType: 'big',
            effectField: 'stepTitleEffect'
        },
        {
            fieldName: 'step1Description',
            label: 'Step 1 Description',
            type: 'textarea',
            placeholder: 'I don\'t coach you through your mind...',
            rows: 3,
            fontField: 'stepDescriptionFont',
            fontType: 'small'
        },
        {
            fieldName: 'step2Number',
            label: 'Step 2 Number',
            type: 'input',
            placeholder: '02',
            fontField: 'stepNumberFont',
            fontType: 'big',
            effectField: 'stepNumberEffect'
        },
        {
            fieldName: 'step2Title',
            label: 'Step 2 Title',
            type: 'textarea',
            placeholder: 'Feminine Leadership Mentorship',
            rows: 2,
            fontField: 'stepTitleFont',
            fontType: 'big',
            effectField: 'stepTitleEffect'
        },
        {
            fieldName: 'step2Description',
            label: 'Step 2 Description',
            type: 'textarea',
            placeholder: 'I mentor you to expand your business...',
            rows: 3,
            fontField: 'stepDescriptionFont',
            fontType: 'small'
        },
        {
            fieldName: 'step3Number',
            label: 'Step 3 Number',
            type: 'input',
            placeholder: '03',
            fontField: 'stepNumberFont',
            fontType: 'big',
            effectField: 'stepNumberEffect'
        },
        {
            fieldName: 'step3Title',
            label: 'Step 3 Title',
            type: 'textarea',
            placeholder: 'Deep Feminine Energetics and Archetype Work',
            rows: 2,
            fontField: 'stepTitleFont',
            fontType: 'big',
            effectField: 'stepTitleEffect'
        },
        {
            fieldName: 'step3Description',
            label: 'Step 3 Description',
            type: 'textarea',
            placeholder: 'I teach you how to live, love, and lead...',
            rows: 3,
            fontField: 'stepDescriptionFont',
            fontType: 'small'
        }
    ];

    // Color Picker Configuration
    const colorConfig = [
        [
            {
                fieldName: 'mainTitleColor',
                label: 'Main Title',
                defaultValue: '#FFFFFF',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'mainDescriptionColor',
                label: 'Main Description',
                defaultValue: '#E0E0E0',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'additionalTextColor',
                label: 'Additional Text',
                defaultValue: '#FFFFFF',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'stepNumberColor',
                label: 'Step Numbers',
                defaultValue: '#FFFFFF',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'stepTitleColor',
                label: 'Step Titles',
                defaultValue: '#FFFFFF',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'stepDescriptionColor',
                label: 'Step Descriptions',
                defaultValue: '#D0D0D0',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#000000',
                icon: '/img/background.svg'
            }
        ]
    ];

    // Font Settings Configuration
    const fontConfig = [
        {
            label: 'Main Title',
            sizeField: 'mainTitleSize',
            weightField: 'mainTitleWeight',
            defaultSize: '48',
            defaultWeight: '400'
        },
        {
            label: 'Main Description',
            sizeField: 'mainDescriptionSize',
            weightField: 'mainDescriptionWeight',
            defaultSize: '18',
            defaultWeight: '400'
        },
        {
            label: 'Additional Text',
            sizeField: 'additionalTextSize',
            weightField: 'additionalTextWeight',
            defaultSize: '16',
            defaultWeight: '300'
        },
        {
            label: 'Step Numbers',
            sizeField: 'stepNumberSize',
            weightField: 'stepNumberWeight',
            defaultSize: '72',
            defaultWeight: '300'
        },
        {
            label: 'Step Titles',
            sizeField: 'stepTitleSize',
            weightField: 'stepTitleWeight',
            defaultSize: '24',
            defaultWeight: '400'
        },
        {
            label: 'Step Descriptions',
            sizeField: 'stepDescriptionSize',
            weightField: 'stepDescriptionWeight',
            defaultSize: '14',
            defaultWeight: '300'
        }
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties für Methodology Widget
    const customLayoutProperties = [
        {
            section: 'Content Settings',
            icon: 'fa-align-left',
            properties: [
                {
                    id: 'contentSpacing',
                    label: 'Content Spacing',
                    min: 20,
                    max: 120,
                    step: 5,
                    unit: 'px',
                    icon: '📄',
                    description: 'Spacing between main content sections'
                },
                {
                    id: 'stepSpacing',
                    label: 'Step Spacing',
                    min: 15,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '📋',
                    description: 'Spacing between methodology steps'
                }
            ]
        }
    ];

    // Advanced Settings für weitere Properties
    const advancedConfig = [
        {
            fieldName: 'borderRadius',
            label: 'Border Radius',
            type: 'number',
            defaultValue: '0',
            icon: '/img/element.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Layout Selector */}
              
                
                <LayoutSelector
                    widgetType="methodologyWidget"
                    currentLayout={editData.layoutType || 'classic-methodology'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* 🚀 PROFILE IMAGE CONTAINER - mit Size Controls */}
                <ImageContainer
                    label="Profile Image"
                    fieldName="profileImage"
                    currentImage={editData.profileImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingProfile}
                    setIsUploading={setIsUploadingProfile}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Width',
                            fieldName: 'profileImageWidth',
                            defaultValue: 400,
                            min: 200,
                            max: 600,
                            step: 20,
                            unit: 'px',
                            description: 'Width of the profile image'
                        },
                        {
                            type: 'size',
                            label: 'Image Height',
                            fieldName: 'profileImageHeight',
                            defaultValue: 500,
                            min: 300,
                            max: 700,
                            step: 20,
                            unit: 'px',
                            description: 'Height of the profile image'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* 🚀 UNDERLAY DECORATION CONTAINER - mit Position, Size, Opacity & Rotation */}
                <ImageContainer
                    label="Underlay Decoration"
                    fieldName="underlayImage"
                    currentImage={editData.underlayImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingUnderlay}
                    setIsUploading={setIsUploadingUnderlay}
                    showToggle={true}
                    toggleLabel="Show Underlay"
                    toggleFieldName="showUnderlay"
                    toggleChecked={editData.showUnderlay || false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'select',
                            label: 'Position',
                            fieldName: 'underlayPosition',
                            defaultValue: 'bottom-right',
                            options: [
                                { value: 'top-left', label: '↖️ Top Left' },
                                { value: 'top-right', label: '↗️ Top Right' },
                                { value: 'bottom-left', label: '↙️ Bottom Left' },
                                { value: 'bottom-right', label: '↘️ Bottom Right' }
                            ]
                        },
                        {
                            type: 'size',
                            label: 'Underlay Size',
                            fieldName: 'underlaySize',
                            defaultValue: 200,
                            min: 100,
                            max: 400,
                            step: 10,
                            unit: 'px',
                            description: 'Size of the underlay decoration'
                        },
                        {
                            type: 'size',
                            label: 'Underlay Opacity',
                            fieldName: 'underlayOpacity',
                            defaultValue: 30,
                            min: 10,
                            max: 100,
                            step: 10,
                            unit: '%',
                            description: 'Transparency of the underlay',
                            convertValue: true
                        },
                        {
                            type: 'size',
                            label: 'Rotation',
                            fieldName: 'underlayRotation',
                            defaultValue: 0,
                            min: 0,
                            max: 360,
                            step: 15,
                            unit: '°',
                            description: 'Rotation angle of the underlay'
                        },
                        {
                            type: 'toggle',
                            label: 'Invert Colors',
                            fieldName: 'underlayInvert'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* 🚀 REVOLUTIONARY MAIN CONTENT TEXT SETTINGS */}
                <TextSettingsSection
                    textConfig={mainTextConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />



                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* Animation Settings - Universal Component */}
                <AnimationSettingsSection 
                    editData={editData}
                    onUpdate={onUpdate}
                    showSpeedControls={true}
                    showDelayControls={true}
                    defaultExpanded={false}
                />

                {/* More Settings Section - Colors, Fonts, Advanced */}
                <MoreSettingsSection
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default MethodologyWidgetEdit;
